import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import ProductCard from './ProductCard';
import { FaHeart as EmptyHeart, FaHeart as FilledHeart, FaArrowLeft, FaWhatsapp, FaFilter, FaTimes, FaPhone, FaStar, FaRegStar, FaStarHalfAlt, FaShoppingCart, FaComments, FaShare, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp, faFacebookMessenger } from '@fortawesome/free-brands-svg-icons';
import slugify from 'slugify';
import { v4 as uuidv4 } from 'uuid';
import ReactPixel from 'react-facebook-pixel';
import ReactGA from "react-ga4";
import ReactMarkdown from 'react-markdown';
import { Helmet } from 'react-helmet';

// Review Modal Component
const ReviewModal = ({ onClose, onSubmit, productName, userName, isLoggedIn }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [customerName, setCustomerName] = useState('');


  useEffect(() => {
    if (isLoggedIn && userName && userName !== 'Anonymous User' && userName !== 'User') {
      setCustomerName(userName);
    } else {
      setCustomerName('');
    }
  }, [userName, isLoggedIn]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(rating, comment, customerName);
  };

  const renderStarInput = (index) => {
    return (
      <span key={index} onClick={() => setRating(index)} className="cursor-pointer hover:scale-110 transition-transform">
        {rating >= index ? <FaStar /> : <FaRegStar />}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-[1000]" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      <div className="bg-white p-8 rounded-lg shadow-2xl w-[90%] max-w-[500px] relative animate-[fadeInScale_0.3s_ease-out_forwards]">
        <button onClick={onClose} className="absolute top-4 right-4 text-2xl text-gray-500 hover:text-gray-800 transition-colors">
          <FaTimes />
        </button>
        <h2 className="text-center mb-6 text-gray-800 text-2xl font-bold">{productName} এর জন্য একটি রিভিউ যোগ করুন</h2>
        <form onSubmit={handleSubmit}>
          <div className="flex justify-center items-center mb-5 text-3xl text-yellow-400">
            {[1, 2, 3, 4, 5].map((index) => renderStarInput(index))}
            <span className="ml-3 text-base text-gray-600">({rating} Star{rating !== 1 ? 's' : ''})</span>
          </div>
          <div className="mb-5">
            <label htmlFor="customerName" className="block mb-2 font-bold text-gray-700">আপনার নাম:</label>
            <input
              type="text"
              id="customerName"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="আপনার নাম লিখুন"
              required
              className="w-full p-3 border border-gray-300 rounded-md text-base focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            />
          </div>
          <div className="mb-5">
            <label htmlFor="comment" className="block mb-2 font-bold text-gray-700">আপনার মন্তব্য:</label>
            <textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows="5"
              placeholder="আপনার মতামত লিখুন..."
              required
              className="w-full p-3 border border-gray-300 rounded-md text-base resize-y min-h-[100px] focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            ></textarea>
          </div>
          <div className="flex justify-end gap-4 mt-6">
            <button type="submit" className="px-6 py-3 bg-green-600 text-white rounded-md text-base hover:bg-green-700 transition-all hover:-translate-y-0.5">
              জমা দিন
            </button>
            <button type="button" onClick={onClose} className="px-6 py-3 bg-gray-600 text-white rounded-md text-base hover:bg-gray-700 transition-all hover:-translate-y-0.5">
              বাতিল করুন
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Alibaba-style Image Zoom Component ───────────────────────────────────────
const ZOOM_FACTOR = 2.5;
const LENS_SIZE = 120; // px

const ImageZoom = ({ src, alt }) => {
  const containerRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const [lensStyle, setLensStyle] = useState({});
  const [zoomBgStyle, setZoomBgStyle] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleMouseMove = (e) => {
    if (isMobile) return;
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const halfLens = LENS_SIZE / 2;

    let lensX = mouseX - halfLens;
    let lensY = mouseY - halfLens;
    lensX = Math.max(0, Math.min(lensX, rect.width - LENS_SIZE));
    lensY = Math.max(0, Math.min(lensY, rect.height - LENS_SIZE));

    const bgX = -((lensX / rect.width) * rect.width * ZOOM_FACTOR);
    const bgY = -((lensY / rect.height) * rect.height * ZOOM_FACTOR);

    setLensStyle({
      left: lensX,
      top: lensY,
      width: LENS_SIZE,
      height: LENS_SIZE,
    });

    setZoomBgStyle({
      backgroundImage: `url(${src})`,
      backgroundSize: `${rect.width * ZOOM_FACTOR}px ${rect.height * ZOOM_FACTOR}px`,
      backgroundPosition: `${bgX}px ${bgY}px`,
      backgroundRepeat: 'no-repeat',
    });
  };

  // Mobile: full-width with 4:3 aspect ratio, no border, no overflow
  if (isMobile) {
    return (
      <div
        style={{
          width: '100%',
          aspectRatio: '4/3',
          overflow: 'hidden',
          display: 'block',
          marginBottom: '6px',
        }}
      >
        <img
          src={src}
          alt={alt}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            transform: 'rotate(90deg)',
            transformOrigin: 'center center',
          }}
          draggable={false}
        />
      </div>
    );
  }

  return (
    <div className="relative" style={{ userSelect: 'none' }}>
      {/* Main image container */}
      <div
        ref={containerRef}
        className="relative bg-white border border-gray-300 rounded-lg overflow-hidden cursor-crosshair mb-4"
        style={{ height: '500px' }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onMouseMove={handleMouseMove}
      >
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          style={{ transform: 'rotate(90deg)', transformOrigin: 'center center' }}
          draggable={false}
        />

        {/* Lens overlay */}
        {isHovering && (
          <div
            className="absolute pointer-events-none border-2 border-orange-400 bg-orange-100 bg-opacity-30"
            style={{
              ...lensStyle,
              boxShadow: '0 0 0 1px rgba(234,88,12,0.3)',
              zIndex: 10,
            }}
          />
        )}
      </div>

      {/* Zoom popup */}
      {isHovering && (
        <div
          className="absolute top-0 rounded-lg border border-gray-300 shadow-2xl overflow-hidden bg-white z-50"
          style={{
            left: 'calc(100% + 16px)',
            width: '420px',
            height: '500px',
            ...zoomBgStyle,
          }}
        >
          <div className="absolute top-2 right-2 bg-black bg-opacity-40 text-white text-xs px-2 py-0.5 rounded">
            {ZOOM_FACTOR}× zoom
          </div>
        </div>
      )}
    </div>
  );
};
// ──────────────────────────────────────────────────────────────────────────────

function ProductDetails({ addToCart, cartItem, toggleFavorite, isFavorite, getAuthHeaders }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [eventId] = useState(uuidv4());

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeImage, setActiveImage] = useState('');
  const [flyingCart, setFlyingCart] = useState(null);
  const addCartButtonRef = useRef(null);

  const [activeTab, setActiveTab] = useState('specification');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');

  const [remainingTime, setRemainingTime] = useState(0);
  const [userSessionId, setUserSessionId] = useState(null);
  const [timerActive, setTimerActive] = useState(false);

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [currentUserName, setCurrentUserName] = useState('');
  const [showSizeChart, setShowSizeChart] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [relatedScrollPosition, setRelatedScrollPosition] = useState(0);
  const relatedContainerRef = useRef(null);

  const [pdpSettings, setPdpSettings] = useState({
    callButton: { enabled: true, text: '📞 Call', phoneNumber: '+8801307104103' },
    whatsappButton: { enabled: true, text: 'WhatsApp Us', phoneNumber: '+8801307104103' },
    messengerButton: { enabled: true, text: 'Chat on Messenger', messengerLink: 'https://m.me/elevateemart' },
    secureTransaction: { enabled: true, text: '🔒 Secure transaction' },
    cashOnDelivery: { enabled: true, text: '✓ Cash on Delivery available' },
  });

  const TIMER_RESET_DAYS = 5;
  const memoizedGetAuthHeaders = useCallback(getAuthHeaders, [getAuthHeaders]);
  const API_BASE_URL = 'https://aman-life-style-bd-main-server.sparkx-ai.shop';

  const checkLoginStatus = useCallback(() => {
    const token = localStorage.getItem('authToken');
    setIsLoggedIn(!!token);
  }, []);

  const fetchUserProfile = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setCurrentUserName('');
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/user-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setCurrentUserName(userData.name || 'Anonymous User');
      } else {
        setCurrentUserName('Anonymous User');
      }
    } catch (error) {
      setCurrentUserName('Anonymous User');
    }
  }, []);

  // Share functionality
  const handleShare = async () => {
    const titleSlug = slugify(product.name, { lower: true, strict: true, trim: true });
    const shareUrl = `https://www.amanlifestylebd.com/product/${id}/${titleSlug}`;
    const shareText = `Check out ${product.name}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: shareText,
          url: shareUrl,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    }
  };

  // Related products scroll handlers
  const scrollRelatedProducts = (direction) => {
    if (relatedContainerRef.current) {
      const scrollAmount = 300;
      const newPosition = direction === 'left'
        ? relatedScrollPosition - scrollAmount
        : relatedScrollPosition + scrollAmount;

      relatedContainerRef.current.scrollTo({
        left: newPosition,
        behavior: 'smooth'
      });
      setRelatedScrollPosition(newPosition);
    }
  };

  // Product Details Page Settings fetch useEffect
  useEffect(() => {
    const fetchPDPSettings = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/product-details-settings`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.settings) {
            setPdpSettings(data.settings);
          }
        }
      } catch (error) {
        console.error('Error fetching PDP settings:', error);
      }
    };

    fetchPDPSettings();
  }, []);

  // Product fetch useEffect
  useEffect(() => {
    const fetchProductData = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = `https://aman-life-style-bd-main-server.sparkx-ai.shop/products/${id}`;
        const response = await fetch(url, {
          headers: memoizedGetAuthHeaders(),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setProduct(data);
        setActiveImage(data.galleryImages?.[0] || data.image);
        setSelectedColor(data.colors?.[0]?.hex || '');
        setSelectedSize(data.sizes?.[0] || '');
      } catch (err) {
        setError("পণ্যটি লোড করা যায়নি। অনুগ্রহ করে পরে আবার চেষ্টা করুন।");
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProductData();
      checkLoginStatus();
      fetchUserProfile();
    }
  }, [id, memoizedGetAuthHeaders, checkLoginStatus, fetchUserProfile]);

  // Related Products fetch useEffect
  useEffect(() => {
    const fetchRelatedProducts = async () => {
      if (!product) return;

      try {
        const response = await fetch(
          `https://aman-life-style-bd-main-server.sparkx-ai.shop/products?category=${product.category}&limit=10`,
          {
            headers: memoizedGetAuthHeaders(),
          }
        );

        if (response.ok) {
          const data = await response.json();
          const filteredProducts = data.products.filter(p => p.productId !== product.productId);
          setRelatedProducts(filteredProducts.slice(0, 10));
        }
      } catch (error) {
        console.error('Error fetching related products:', error);
      }
    };

    fetchRelatedProducts();
  }, [product, memoizedGetAuthHeaders]);

  const hasFiredRef = useRef(false);

  useEffect(() => {
    if (hasFiredRef.current) return;

    function getFbClickId() {
      const match = document.cookie.match(/_fbc=([^;]*)/);
      return match ? match[1] : null;
    }

    function getFbBrowserId() {
      const match = document.cookie.match(/_fbp=([^;]*)/);
      return match ? match[1] : null;
    }

    const externalId = localStorage.getItem('client_id');
    const fbc = getFbClickId();
    const fbp = getFbBrowserId();

    const userData = { fbc, fbp, external_id: externalId };

    if (product && id) {
      const eventTime = Math.floor(Date.now() / 1000);

      ReactPixel.track('ViewContent', {
        content_name: product.name,
        content_category: product.category,
        content_ids: [product.productId],
        content_type: 'product',
        value: product.price,
        currency: 'BDT',
        event_id: eventId
      }, userData);

      ReactGA.event('view_item', {
        currency: 'BDT',
        value: product.price,
        items: [{
          item_id: product.productId,
          item_name: product.name,
          item_category: product.category,
          price: product.price,
          quantity: 1
        }],
        event_id: eventId,
      });

      fetch('https://aman-life-style-bd-main-server.sparkx-ai.shop/api/track-viewcontent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_name: 'ViewContent',
          productId: product.productId,
          name: product.name,
          category: product.category,
          price: product.price,
          event_id: eventId,
          event_time: eventTime,
          fbc, fbp,
          external_id: externalId,
        }),
      }).then(res => res.json())
        .then(data => console.log('ViewContent sent to Flask:', data))
        .catch(err => console.error('Error sending to Flask:', err));
      hasFiredRef.current = true;
    }
  }, [product, eventId, id]);

  useEffect(() => {
    let currentUserId = localStorage.getItem('user_session_id');
    if (!currentUserId) {
      currentUserId = uuidv4();
      localStorage.setItem('user_session_id', currentUserId);
    }
    setUserSessionId(currentUserId);

    let interval;

    if (product && product.timer && product.time != null) {
      setTimerActive(true);
      const productTimerKey = `product_timer_${product.productId}_${currentUserId}`;
      const lastExpiredKey = `product_last_expired_${product.productId}_${currentUserId}`;

      let storedEndTime = localStorage.getItem(productTimerKey);
      let newEndTime;
      const now = new Date().getTime();

      if (storedEndTime) {
        newEndTime = parseInt(storedEndTime, 10);
        const lastExpiredTime = localStorage.getItem(lastExpiredKey);

        if (now >= newEndTime) {
          if (lastExpiredTime) {
            const timeSinceLastExpired = now - parseInt(lastExpiredTime, 10);
            if (timeSinceLastExpired >= TIMER_RESET_DAYS * 24 * 60 * 60 * 1000) {
              newEndTime = now + product.time * 60 * 1000;
              localStorage.setItem(productTimerKey, newEndTime.toString());
              localStorage.removeItem(lastExpiredKey);
            } else {
              setTimerActive(false);
              setRemainingTime(0);
              return;
            }
          } else {
            localStorage.setItem(lastExpiredKey, now.toString());
            setTimerActive(false);
            setRemainingTime(0);
            return;
          }
        }
      } else {
        newEndTime = now + product.time * 60 * 1000;
        localStorage.setItem(productTimerKey, newEndTime.toString());
        localStorage.removeItem(lastExpiredKey);
      }

      interval = setInterval(() => {
        const currentTime = new Date().getTime();
        const timeLeft = newEndTime - currentTime;

        if (timeLeft <= 0) {
          clearInterval(interval);
          setRemainingTime(0);
          setTimerActive(false);
          if (!localStorage.getItem(lastExpiredKey)) {
            localStorage.setItem(lastExpiredKey, currentTime.toString());
          }
        } else {
          setRemainingTime(timeLeft);
        }
      }, 1000);
    } else {
      setTimerActive(false);
      setRemainingTime(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [product, TIMER_RESET_DAYS]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (flyingCart) {
      const timer = setTimeout(() => setFlyingCart(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [flyingCart]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
        <p className="text-center">Loading product...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
        <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-4">
          <FaArrowLeft className="mr-2" /> Back to Products
        </Link>
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
        <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-4">
          <FaArrowLeft className="mr-2" /> Back to Products
        </Link>
        <p>Product not found!</p>
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart(product);

    function getFbClickId() {
      const match = document.cookie.match(/_fbc=([^;]*)/);
      return match ? match[1] : null;
    }

    function getFbBrowserId() {
      const match = document.cookie.match(/_fbp=([^;]*)/);
      return match ? match[1] : null;
    }

    const externalId = localStorage.getItem('client_id');
    const fbc = getFbClickId();
    const fbp = getFbBrowserId();
    const userData = { fbc, fbp, external_id: externalId };
    const eventTime = Math.floor(Date.now() / 1000);

    ReactPixel.track('AddToCart', {
      content_name: product.name,
      content_category: product.category,
      content_ids: [product.productId],
      content_type: 'product',
      value: product.price,
      event_id: eventId,
      currency: 'BDT'
    }, userData);

    ReactGA.event('add_to_cart', {
      currency: 'BDT',
      value: product.price,
      items: [{
        item_id: product.productId,
        item_name: product.name,
        item_category: product.category,
        price: product.price,
        quantity: 1
      }],
      event_id: eventId,
    });

    if (addCartButtonRef.current) {
      const rect = addCartButtonRef.current.getBoundingClientRect();
      setFlyingCart({
        id: Date.now(),
        startX: rect.left + rect.width / 2,
        startY: rect.top + rect.height / 2,
      });
    }

    fetch('https://aman-life-style-bd-main-server.sparkx-ai.shop/api/track-addtocart', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_name: 'AddToCart',
        productId: product.productId,
        name: product.name,
        price: product.price,
        color: selectedColor,
        size: selectedSize,
        event_id: eventId,
        event_time: eventTime,
        fbc, fbp,
        external_id: externalId,
      }),
    }).then(res => res.json())
      .then(data => console.log('AddToCart sent to Flask:', data))
      .catch(err => console.error('Error sending AddToCart to Flask:', err));
  };

  const hasStock = product.stock > 0;
  const productReviews = product.reviews || [];
  const averageRating = productReviews.length > 0
    ? (productReviews.reduce((acc, r) => acc + r.rating, 0) / productReviews.length)
    : 0;

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (rating >= i) {
        stars.push(<FaStar key={i} />);
      } else if (rating >= i - 0.5) {
        stars.push(<FaStarHalfAlt key={i} />);
      } else {
        stars.push(<FaRegStar key={i} className="text-gray-300" />);
      }
    }
    return stars;
  };

  const handleWhatsAppClick = () => {
    const titleSlug = slugify(product.name, { lower: true, strict: true, trim: true });
    const link = `https://www.amanlifestylebd.com/product/${id}/${titleSlug}`;
    const message = `আমি জানতে চাই আপনাদের ${product.name} প্রোডাক্ট সম্পর্কে ${link}`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappNumber = pdpSettings.whatsappButton.phoneNumber;
    window.open(`https://wa.me/${whatsappNumber}?text=${encodedMessage}`, '_blank');
  };

  const handleMessengerClick = () => {
    const messengerLink = pdpSettings.messengerButton.messengerLink;
    window.open(messengerLink, '_blank');
  };

  const handleClick = () => {
    const phoneNumber = pdpSettings.callButton.phoneNumber;
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleProceedToCheckout = () => {
    const productToAddForCheckout = { ...product, quantity: 1 };
    let cartItemsForNavigation = [];
    const existingIndex = cartItem.findIndex(item => item.productId === productToAddForCheckout.productId);
    if (existingIndex !== -1) {
      cartItemsForNavigation = cartItem.map((item, index) =>
        index === existingIndex ? { ...item, quantity: item.quantity + 1 } : item
      );
    } else {
      cartItemsForNavigation = [...cartItem, productToAddForCheckout];
    }

    const totalForNavigation = cartItemsForNavigation.reduce((acc, item) => {
      const price = typeof item.price === 'number' ? item.price : parseFloat(item.price) || 0;
      const quantity = typeof item.quantity === 'number' ? item.quantity : parseInt(item.quantity) || 0;
      return acc + (price * quantity);
    }, 0);
    addToCart(product);
    navigate('/checkout', {
      state: {
        cartItems: cartItemsForNavigation,
        total: totalForNavigation,
      },
    });
  };

  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const days = Math.floor(totalSeconds / (3600 * 24));
    const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return { days, hours, minutes, seconds };
  };

  const formattedTime = formatTime(remainingTime);

  const schemaData = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": product.name,
    "image": product.image,
    "description": product.description,
    "sku": product.sku || product.id,
    "offers": {
      "@type": "Offer",
      "priceCurrency": "BDT",
      "price": product.price,
      "availability": product.stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
    }
  };

  const handleAddReviewClick = () => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    setShowReviewModal(true);
  };

  const handleReviewSubmit = async (rating, comment, submittedUserName) => {
    try {
      const token = localStorage.getItem('authToken');

      if (!token) {
        setShowReviewModal(false);
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/add-product-review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          productId: product.productId,
          rating,
          comment,
          customerName: submittedUserName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'রিভিউ যোগ করতে সমস্যা হয়েছে।');
      }

      setShowReviewModal(false);
      const updatedProductResponse = await fetch(`https://aman-life-style-bd-main-server.sparkx-ai.shop/products/${id}`, {
        headers: memoizedGetAuthHeaders(),
      });
      if (updatedProductResponse.ok) {
        const updatedProductData = await updatedProductResponse.json();
        setProduct(updatedProductData);
      }
    } catch (err) {
      console.error("রিভিউ সাবমিট করতে সমস্যা:", err);
    }
  };

  return (
    <>
      <Helmet>
        <title>{product.name} | Aman Life Style</title>
        <meta name="description" content={product.description || "Aman Life Style বাংলাদেশের একমাত্র সহজলভ্য ও দ্রুত ডেলিভারি দেওয়া ই-কমার্স প্রতিষ্ঠান"} />
        <meta name="keywords" content={`ElevateMart, সহজলভ্য, দ্রুত ডেলিভারি, ই-কমার্স প্রতিষ্ঠান, ${product.name}, ${product.category}`} />
        <meta property="og:image" content={product.image} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={product.name} />
        <meta property="og:description" content={product.description} />
        <meta property="og:url" content={`https://www.amanlifestylebd.com/product/${id}/${slugify(product.name, { lower: true, strict: true, trim: true })}`} />
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      {/* Main Container - Amazon Style */}
      <div className="bg-white pdp-mobile-root" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-2 py-1.5 md:px-4 md:py-3 border-b border-gray-200">
          <Link to="/" className="inline-flex items-center text-xs md:text-sm text-blue-600 hover:text-orange-600 hover:underline">
            <FaArrowLeft className="mr-1 md:mr-2" /> Back to results
          </Link>
        </div>

        {/* Product Section */}
        <div className="max-w-7xl mx-auto px-0 md:px-4 py-0 md:py-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 md:gap-8">

            {/* Left Column - Images */}
            <div className="lg:col-span-5 border-0 md:border md:border-gray-200 rounded-none md:rounded-lg p-0 md:p-4 bg-white" style={{ overflow: 'visible' }}>
              <div className="sticky top-4" style={{ overflow: 'visible' }}>
                {/* Badges and Favorite */}
                <div className="relative" style={{ overflow: 'visible' }}>
                  {product.discount && !product.isPopular && (
                    <div className="absolute top-1.5 left-1.5 md:top-4 md:left-4 bg-red-600 text-white px-2 py-0.5 md:px-3 md:py-1 rounded text-xs md:text-sm font-bold z-10">
                      {product.discount}
                    </div>
                  )}
                  {product.isPopular && (
                    <div className="absolute top-1.5 left-1.5 md:top-4 md:left-4 bg-orange-500 text-white px-2 py-0.5 md:px-3 md:py-1 rounded text-xs md:text-sm font-bold z-10">
                      Best Seller
                    </div>
                  )}
                  <button
                    onClick={() => toggleFavorite(product.productId)}
                    className="absolute top-1.5 right-1.5 md:top-4 md:right-4 text-xl md:text-2xl z-20 transition-colors"
                  >
                    {isFavorite(product.productId) ? (
                      <FilledHeart className="text-red-500" />
                    ) : (
                      <EmptyHeart className="text-gray-400 hover:text-red-500" />
                    )}
                  </button>

                  {/* Image with zoom on desktop, full-width on mobile */}
                  <ImageZoom src={activeImage} alt={product.name} />
                </div>

                {/* Thumbnails */}
                <div className="flex justify-center md:justify-start gap-1.5 md:gap-2 overflow-x-auto pb-1 md:pb-2 mt-1 md:mt-0 px-2 md:px-0">
                  {product.galleryImages && product.galleryImages.length > 0 ? (
                    product.galleryImages.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveImage(img)}
                        className={`flex-shrink-0 w-12 h-12 md:w-16 md:h-16 border-2 rounded overflow-hidden hover:border-orange-500 transition-colors ${activeImage === img ? 'border-orange-500' : 'border-gray-300'
                          }`}
                      >
                        <img
                          src={img}
                          alt={`${product.name} - ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))
                  ) : (
                    <button
                      onClick={() => setActiveImage(product.image)}
                      className="flex-shrink-0 w-12 h-12 md:w-16 md:h-16 border-2 border-orange-500 rounded overflow-hidden"
                    >
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Middle Column - Product Info */}
            <div className="lg:col-span-4 border border-gray-200 rounded-none md:rounded-lg p-3 md:p-6 bg-white mx-0 mt-1 md:mt-0 md:mx-0">
              <h1 className="text-base md:text-2xl font-normal text-gray-900 mb-1.5 md:mb-2 leading-snug">{product.name}</h1>

              {/* Timer */}
              {timerActive && remainingTime > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-2 md:p-3 mb-2 md:mb-4">
                  <div className="flex items-center justify-center gap-1.5 md:gap-2 text-xs md:text-sm font-semibold text-red-700">
                    <span>⏰ Deal ends in:</span>
                    <div className="flex gap-0.5 md:gap-1">
                      {formattedTime.days > 0 && (
                        <span className="bg-white px-1.5 py-0.5 md:px-2 md:py-1 rounded text-xs md:text-sm">{String(formattedTime.days).padStart(2, '0')}d</span>
                      )}
                      {(formattedTime.hours > 0 || formattedTime.days > 0) && (
                        <span className="bg-white px-1.5 py-0.5 md:px-2 md:py-1 rounded text-xs md:text-sm">{String(formattedTime.hours).padStart(2, '0')}h</span>
                      )}
                      <span className="bg-white px-1.5 py-0.5 md:px-2 md:py-1 rounded text-xs md:text-sm">{String(formattedTime.minutes).padStart(2, '0')}m</span>
                      <span className="bg-white px-1.5 py-0.5 md:px-2 md:py-1 rounded text-xs md:text-sm">{String(formattedTime.seconds).padStart(2, '0')}s</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Rating */}
              <div className="flex items-center gap-1.5 md:gap-2 mb-2 md:mb-4">
                <div className="flex items-center text-orange-500 text-xs md:text-base">
                  {renderStars(averageRating)}
                </div>
                {productReviews.length > 0 ? (
                  <span className="text-xs md:text-sm text-blue-600 hover:text-orange-600 cursor-pointer">
                    {averageRating.toFixed(1)} ({productReviews.length})
                  </span>
                ) : (
                  <span className="text-xs md:text-sm text-gray-600">No reviews yet</span>
                )}
              </div>

              {/* Stock Status */}
              <div className={`text-sm md:text-lg font-semibold mb-2 md:mb-4 ${hasStock ? 'text-green-700' : 'text-red-600'}`}>
                {hasStock ? `In Stock (${product.stock})` : 'Out of Stock'}
              </div>

              {/* Buyers Info */}
              {product.buyers > 0 && (
                <p className="text-xs md:text-sm text-gray-600 mb-2 md:mb-4">
                  {product.buyers} people bought this
                </p>
              )}

              {/* Price */}
              <div className="mb-3 md:mb-6">
                <div className="flex items-baseline gap-2 md:gap-3">
                  <span className="mobile-price text-xl md:text-3xl font-normal text-red-700">৳{product.price}</span>
                  {product.originalPrice && product.originalPrice > product.price && (
                    <>
                      <span className="text-sm md:text-lg text-gray-500 line-through">৳{product.originalPrice}</span>
                      <span className="text-xs md:text-sm text-red-700 font-semibold">
                        -{Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}%
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Color Selection */}
              {product.isColorAvailable && product.colors && product.colors.length > 0 && (
                <div className="mb-3 md:mb-6 pb-3 md:pb-6 border-b border-gray-200">
                  <h3 className="text-xs md:text-sm font-bold text-gray-900 mb-1.5 md:mb-2">Color: {product.colors.find(c => c.hex === selectedColor)?.name || ''}</h3>
                  <div className="flex gap-1.5 md:gap-2 flex-wrap">
                    {product.colors.map((color, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedColor(color.hex)}
                        className={`flex items-center gap-1.5 px-2 py-1 md:px-3 md:py-2 border-2 rounded-lg transition-all text-xs md:text-sm ${selectedColor === color.hex ? 'border-orange-500 bg-orange-50' : 'border-gray-300 hover:border-gray-400'
                          }`}
                        title={color.name}
                      >
                        <div
                          className="w-4 h-4 md:w-6 md:h-6 rounded-full border border-gray-300"
                          style={{ backgroundColor: color.hex }}
                        />
                        <span>{color.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Size Selection */}
              {product.isSizeAvailable && product.sizes && product.sizes.length > 0 && (
                <div className="mb-3 md:mb-6 pb-3 md:pb-6 border-b border-gray-200">
                  <h3 className="text-xs md:text-sm font-bold text-gray-900 mb-1.5 md:mb-2">
                    Size: <span className="text-orange-600">{selectedSize}</span>
                  </h3>

                  {/* Size buttons */}
                  <div className="flex gap-1.5 md:gap-2 flex-wrap mb-2 md:mb-4">
                    {product.sizes.map((size, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedSize(size)}
                        className={`px-2.5 py-1 md:px-4 md:py-2 border-2 rounded-lg transition-all text-xs md:text-sm ${selectedSize === size
                          ? 'border-orange-500 bg-orange-50 font-semibold'
                          : 'border-gray-300 hover:border-gray-400'
                          }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>

                  {/* Size Chart Table */}
                  {product.sizeChart && product.sizeChart.length > 0 && product.sizeChartColumns && product.sizeChartColumns.length > 0 && (
                    <div className="mt-2 md:mt-3">
                      <button
                        onClick={() => setShowSizeChart(prev => !prev)}
                        className="text-xs text-blue-600 hover:text-blue-800 underline mb-1.5 md:mb-2 flex items-center gap-1"
                      >
                        📏 {showSizeChart ? 'Hide' : 'View'} Size Chart
                      </button>

                      {showSizeChart && (
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          {/* INCH table */}
                          <div className="overflow-x-auto">
                            <div className="bg-yellow-500 px-2 py-0.5 md:px-3 md:py-1">
                              <span className="text-white text-xs font-bold">INCH</span>
                            </div>
                            <table className="w-full text-xs border-collapse">
                              <thead className="bg-yellow-50">
                                <tr>
                                  <th className="border border-gray-200 px-2 py-1.5 md:px-3 md:py-2 text-left font-semibold text-gray-700">Size</th>
                                  {product.sizeChartColumns.map((col) => (
                                    <th key={col} className="border border-gray-200 px-2 py-1.5 md:px-3 md:py-2 text-center font-semibold text-gray-700">
                                      {col}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {product.sizeChart.map((row, idx) => (
                                  <tr
                                    key={idx}
                                    className={`${row.size === selectedSize ? 'bg-orange-50 font-semibold' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                                  >
                                    <td className="border border-gray-200 px-2 py-1.5 md:px-3 md:py-2 font-semibold text-gray-800">
                                      {row.size === selectedSize ? `▶ ${row.size}` : row.size}
                                    </td>
                                    {product.sizeChartColumns.map((col) => (
                                      <td key={col} className="border border-gray-200 px-2 py-1.5 md:px-3 md:py-2 text-center text-gray-700">
                                        {row[`${col}_inch`] || '—'}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* CM table */}
                          <div className="overflow-x-auto mt-1 md:mt-2">
                            <div className="bg-green-600 px-2 py-0.5 md:px-3 md:py-1">
                              <span className="text-white text-xs font-bold">CM</span>
                            </div>
                            <table className="w-full text-xs border-collapse">
                              <thead className="bg-green-50">
                                <tr>
                                  <th className="border border-gray-200 px-2 py-1.5 md:px-3 md:py-2 text-left font-semibold text-gray-700">Size</th>
                                  {product.sizeChartColumns.map((col) => (
                                    <th key={col} className="border border-gray-200 px-2 py-1.5 md:px-3 md:py-2 text-center font-semibold text-gray-700">
                                      {col}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {product.sizeChart.map((row, idx) => (
                                  <tr
                                    key={idx}
                                    className={`${row.size === selectedSize ? 'bg-orange-50 font-semibold' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                                  >
                                    <td className="border border-gray-200 px-2 py-1.5 md:px-3 md:py-2 font-semibold text-gray-800">
                                      {row.size === selectedSize ? `▶ ${row.size}` : row.size}
                                    </td>
                                    {product.sizeChartColumns.map((col) => (
                                      <td key={col} className="border border-gray-200 px-2 py-1.5 md:px-3 md:py-2 text-center text-gray-700">
                                        {row[`${col}_cm`] || '—'}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          <p className="text-xs text-gray-400 px-2 py-1 md:px-3 md:py-2 bg-gray-50">
                            * Expected deviation &lt; 3%
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Description Preview */}
              {product.description && (
                <div className="mb-3 md:mb-6 bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                  <h3 className="text-xs md:text-sm font-bold text-gray-900 px-3 pt-2.5 pb-1.5 md:px-4 md:pt-4 md:pb-2">About this item</h3>
                  <div className="relative">
                    <div className="text-xs md:text-sm text-gray-700 leading-relaxed px-3 pb-3 md:px-4 md:pb-4" style={{ maxHeight: '80px', overflow: 'hidden' }}>
                      {product.description.split('\n').slice(0, 6).map((line, index) => {
                        if (!line.trim()) return null;
                        const cleanLine = line.trim().startsWith('**') ? line.trim().substring(2) : line.trim();
                        return (
                          <div key={index} className="flex gap-1.5 mb-0.5 md:gap-2 md:mb-1">
                            <span className="text-gray-400">•</span>
                            <span>{cleanLine}</span>
                          </div>
                        );
                      })}
                    </div>
                    {/* Blur gradient overlay */}
                    <div
                      className="absolute bottom-0 left-0 right-0 flex items-end justify-center pb-2 md:pb-3"
                      style={{
                        height: '60px',
                        background: 'linear-gradient(to bottom, transparent, rgba(249,250,251,0.85) 50%, rgb(249,250,251))',
                      }}
                    >
                      <button
                        onClick={() => {
                          setActiveTab('specification');
                          setTimeout(() => {
                            document.getElementById('product-tabs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }, 50);
                        }}
                        className="text-xs font-semibold text-orange-600 hover:text-orange-700 border border-orange-400 bg-white px-2.5 py-0.5 md:px-3 md:py-1 rounded-full shadow-sm hover:shadow transition-all"
                      >
                        See more ▾
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Share Button */}
              <button
                onClick={handleShare}
                className="w-full py-1.5 px-3 md:py-2 md:px-4 bg-white border border-gray-300 rounded-lg text-xs md:text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all flex items-center justify-center gap-1.5 md:gap-2"
              >
                <FaShare className="text-gray-600" />
                Share this product
              </button>
            </div>

            {/* Right Column - Buy Box */}
            <div className="lg:col-span-3">
              <div className="border border-gray-300 rounded-none md:rounded-lg p-2 md:p-4 sticky top-4 bg-white">
                {/* Price & stock info — hidden on mobile, shown on desktop */}
                <div className="hidden md:block mb-4">
                  <div className="text-3xl font-normal text-red-700 mb-2">৳{product.price}</div>
                  {product.originalPrice && product.originalPrice > product.price && (
                    <div className="text-sm text-gray-600">
                      List: <span className="line-through">৳{product.originalPrice}</span>
                    </div>
                  )}
                </div>

                <div className="hidden md:block text-sm mb-4 pb-4 border-b border-gray-200">
                  <div className={`font-semibold ${hasStock ? 'text-green-700' : 'text-red-600'}`}>
                    {hasStock ? '✓ In Stock' : '✗ Out of Stock'}
                  </div>
                  {hasStock && (
                    <div className="text-gray-600 mt-0.5">
                      Ships from Bangladesh
                    </div>
                  )}
                </div>

                {/* Action Buttons - Mobile: Buy Now full row, rest 2x2 grid */}
                <div className="space-y-1.5 md:space-y-3">
                  {/* Buy Now - always full width */}
                  <button
                    ref={addCartButtonRef}
                    onClick={handleProceedToCheckout}
                    disabled={!hasStock}
                    className={`btn-buy-now w-full py-2.5 md:py-3 px-4 rounded-lg font-semibold transition-all text-sm md:text-base ${hasStock
                      ? 'bg-orange-400 hover:bg-orange-500 text-gray-900'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                  >
                    {hasStock ? 'Buy Now' : 'Out of Stock'}
                  </button>

                  {/* Add to Cart - full width on desktop, part of grid on mobile */}
                  <button
                    onClick={handleAddToCart}
                    disabled={!hasStock}
                    className={`btn-add-cart w-full py-2.5 md:py-3 px-4 rounded-lg font-semibold transition-all text-sm md:text-base ${hasStock
                      ? 'bg-yellow-400 hover:bg-yellow-500 text-gray-900'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                  >
                    <FaShoppingCart className="inline mr-1.5 md:mr-2" />
                    {hasStock ? 'Add to Cart' : 'Out of Stock'}
                  </button>

                  {/* Mobile: 2-column grid for remaining buttons */}
                  <div className="grid grid-cols-2 gap-1.5 md:grid-cols-1 md:gap-0 md:space-y-3">
                    {/* Call Button - Conditional */}
                    {pdpSettings.callButton.enabled && (
                      <button
                        onClick={handleClick}
                        className="btn-call py-2 md:py-3 px-2 md:px-4 border-2 border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-all text-gray-900 text-xs md:text-base"
                      >
                        {pdpSettings.callButton.text}
                      </button>
                    )}

                    {/* WhatsApp Button - Conditional */}
                    {pdpSettings.whatsappButton.enabled && (
                      <button
                        onClick={handleWhatsAppClick}
                        className="btn-whatsapp py-2 md:py-3 px-2 md:px-4 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-all text-xs md:text-base"
                      >
                        <FontAwesomeIcon icon={faWhatsapp} className="mr-1 md:mr-2" />
                        {pdpSettings.whatsappButton.text}
                      </button>
                    )}

                    {/* Messenger Button - Conditional - spans full width if alone, or fits in grid */}
                    {pdpSettings.messengerButton.enabled && (
                      <button
                        onClick={handleMessengerClick}
                        className={`btn-messenger py-2 md:py-3 px-2 md:px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-all text-xs md:text-base ${
                          // If both call and whatsapp are enabled, messenger is 3rd item, so it spans 2 cols on mobile
                          pdpSettings.callButton.enabled && pdpSettings.whatsappButton.enabled
                            ? 'col-span-2 md:col-span-1'
                            : ''
                          }`}
                      >
                        <FontAwesomeIcon icon={faFacebookMessenger} className="mr-1 md:mr-2" />
                        {pdpSettings.messengerButton.text}
                      </button>
                    )}
                  </div>
                </div>

                {/* Secure Transaction - Conditional */}
                {(pdpSettings.secureTransaction.enabled || pdpSettings.cashOnDelivery.enabled) && (
                  <div className="mt-2 md:mt-4 pt-2 md:pt-4 border-t border-gray-200 text-xs text-gray-600">
                    {pdpSettings.secureTransaction.enabled && (
                      <div className="flex items-center gap-1 mb-1">
                        <span>{pdpSettings.secureTransaction.text}</span>
                      </div>
                    )}
                    {pdpSettings.cashOnDelivery.enabled && (
                      <div className="flex items-center gap-1">
                        <span>{pdpSettings.cashOnDelivery.text}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Product Details Tabs */}
          <div id="product-tabs" className="mt-4 md:mt-12 border border-gray-200 rounded-lg p-3 md:p-6 bg-white">
            <div className="border-b border-gray-300">
              <div className="flex gap-4 md:gap-8">
                <button
                  onClick={() => setActiveTab('specification')}
                  className={`pb-2 md:pb-3 px-1 font-medium transition-all text-xs md:text-base ${activeTab === 'specification'
                    ? 'text-orange-600 border-b-2 border-orange-600'
                    : 'text-gray-600 hover:text-gray-900'
                    }`}
                >
                  Specifications
                </button>
                <button
                  onClick={() => setActiveTab('reviews')}
                  className={`pb-2 md:pb-3 px-1 font-medium transition-all text-xs md:text-base ${activeTab === 'reviews'
                    ? 'text-orange-600 border-b-2 border-orange-600'
                    : 'text-gray-600 hover:text-gray-900'
                    }`}
                >
                  Reviews ({productReviews.length})
                </button>
              </div>
            </div>

            <div className="py-3 md:py-6">
              {activeTab === 'specification' && (
                <div className="max-w-4xl">
                  {product.specifications && product.specifications.length > 0 ? (
                    <div className="bg-white">
                      <h2 className="text-sm md:text-xl font-bold text-gray-900 mb-2 md:mb-4">Details</h2>
                      <table className="w-full border-collapse">
                        <tbody>
                          {product.specifications.map((spec, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                              <td className="py-1.5 px-2 md:py-3 md:px-4 font-semibold text-gray-700 w-1/3 border-b border-gray-200 text-xs md:text-base">
                                {spec.key}
                              </td>
                              <td className="py-1.5 px-2 md:py-3 md:px-4 text-gray-900 border-b border-gray-200 text-xs md:text-base">
                                {spec.value}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-xs md:text-base text-gray-600">No specifications available for this product.</p>
                  )}

                  {/* Full Description */}
                  {product.description && (
                    <div className="mt-4 md:mt-8">
                      <h2 className="text-sm md:text-xl font-bold text-gray-900 mb-2 md:mb-4">Description</h2>
                      <div className="prose max-w-none">
                        {product.description.split('\n').map((line, index) => {
                          if (!line.trim()) return null;
                          const isSpecialLine = line.trim().startsWith('**');
                          const cleanLine = isSpecialLine ? line.trim().substring(2) : line.trim();

                          return (
                            <div key={index} className="mb-1.5 md:mb-3 flex gap-2 md:gap-3">
                              <span className="text-orange-500 mt-0.5 text-xs md:text-base">
                                {isSpecialLine ? '✨' : '•'}
                              </span>
                              <div className="flex-1">
                                <span className={`text-xs md:text-base ${isSpecialLine ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                                  {cleanLine}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* YouTube Video */}
                  {product.youtubeUrl && (
                    <div className="mt-4 md:mt-8">
                      <h2 className="text-sm md:text-xl font-bold text-gray-900 mb-2 md:mb-4">Video</h2>
                      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                        <iframe
                          src={product.youtubeUrl}
                          title="Product Video"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="absolute top-0 left-0 w-full h-full rounded-lg"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'reviews' && (
                <div className="max-w-4xl">
                  <div className="mb-3 md:mb-6">
                    <h2 className="text-sm md:text-xl font-bold text-gray-900 mb-2 md:mb-4">Reviews</h2>

                    {productReviews.length > 0 ? (
                      <>
                        {/* Rating Summary */}
                        <div className="flex items-center gap-4 md:gap-6 mb-3 md:mb-6 p-2.5 md:p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="text-center">
                            <div className="text-3xl md:text-5xl font-bold text-gray-900">{averageRating.toFixed(1)}</div>
                            <div className="flex items-center justify-center text-orange-500 my-1 md:my-2 text-xs md:text-base">
                              {renderStars(averageRating)}
                            </div>
                            <div className="text-xs md:text-sm text-gray-600">{productReviews.length} ratings</div>
                          </div>
                        </div>

                        {/* Review Cards */}
                        <div className="space-y-2 md:space-y-4">
                          {productReviews.map((review) => (
                            <div key={review.reviewId} className="border border-gray-200 rounded-lg p-2.5 md:p-4 hover:shadow-md transition-shadow">
                              <div className="flex items-center justify-between mb-1 md:mb-2">
                                <span className="font-semibold text-gray-900 text-xs md:text-base">{review.userName}</span>
                                <div className="flex items-center text-orange-500 text-xs md:text-base">
                                  {renderStars(review.rating)}
                                </div>
                              </div>
                              <p className="text-gray-700 leading-relaxed text-xs md:text-base">{review.comment}</p>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <p className="text-xs md:text-base text-gray-600 mb-3 md:mb-6">No reviews yet. Be the first to review this product!</p>
                    )}

                    {/* Add Review Button */}
                    <div className="mt-3 md:mt-6 pt-3 md:pt-6 border-t border-gray-200">
                      <button
                        onClick={handleAddReviewClick}
                        className="px-4 py-2 md:px-6 md:py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold transition-all text-xs md:text-base"
                      >
                        <FaComments className="inline mr-1.5 md:mr-2" />
                        Write a Review
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Flying Cart Animation */}
      {flyingCart && (
        <div
          key={flyingCart.id}
          className="fixed pointer-events-none z-50 text-orange-500 text-2xl"
          style={{
            left: flyingCart.startX,
            top: flyingCart.startY,
            animation: 'flyUpAndFade 3s forwards',
          }}
        >
          <FaShoppingCart /> +1
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && (
        <ReviewModal
          onClose={() => setShowReviewModal(false)}
          onSubmit={handleReviewSubmit}
          productName={product.name}
          userName={currentUserName}
          isLoggedIn={isLoggedIn}
        />
      )}

      {/* Related Products Section */}
      {relatedProducts.length > 0 && (
        <div className="max-w-7xl mx-auto px-2 pb-4 md:px-4 md:pb-8 pdp-mobile-root" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
          <div className="border border-gray-200 rounded-lg p-3 md:p-6 bg-white">
            <h2 className="text-base md:text-2xl font-bold text-gray-900 mb-3 md:mb-6" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
              Related items
            </h2>

            {/* Mobile: 2-column grid showing all products */}
            <div className="grid grid-cols-2 gap-2 md:hidden">
              {relatedProducts.map((relatedProduct) => (
                <div key={relatedProduct.productId}>
                  <ProductCard
                    product={relatedProduct}
                    addToCart={addToCart}
                    toggleFavorite={toggleFavorite}
                    isFavorite={isFavorite}
                    cartItems={cartItem}
                    navigate={navigate}
                  />
                </div>
              ))}
            </div>

            {/* Desktop: horizontal scroll with arrows */}
            <div className="relative hidden md:block">
              {/* Left Arrow */}
              <button
                onClick={() => scrollRelatedProducts('left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white border border-gray-300 rounded-full shadow-md hover:shadow-lg flex items-center justify-center transition-all hover:bg-gray-50"
                style={{ marginLeft: '-20px' }}
              >
                <FaChevronLeft className="text-gray-600" />
              </button>

              {/* Products Container */}
              <div
                ref={relatedContainerRef}
                className="overflow-x-auto scrollbar-hide"
                style={{ scrollBehavior: 'smooth' }}
              >
                <div className="flex gap-4 pb-2">
                  {relatedProducts.map((relatedProduct) => (
                    <div key={relatedProduct.productId} className="flex-shrink-0 w-[280px]">
                      <ProductCard
                        product={relatedProduct}
                        addToCart={addToCart}
                        toggleFavorite={toggleFavorite}
                        isFavorite={isFavorite}
                        cartItems={cartItem}
                        navigate={navigate}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Arrow */}
              <button
                onClick={() => scrollRelatedProducts('right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white border border-gray-300 rounded-full shadow-md hover:shadow-lg flex items-center justify-center transition-all hover:bg-gray-50"
                style={{ marginRight: '-20px' }}
              >
                <FaChevronRight className="text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes flyUpAndFade {
          0% {
            opacity: 1;
            transform: translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateY(-100px);
          }
        }

        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }

        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        /* ── Button Animations ── */

/* Buy Now - orange pulse */
.btn-buy-now {
  animation: pulse-orange 2s infinite;
}
@keyframes pulse-orange {
  0%, 100% { box-shadow: 0 0 0 0 rgba(251, 146, 60, 0.7); }
  50%       { box-shadow: 0 0 0 10px rgba(251, 146, 60, 0); }
}

/* Add to Cart - yellow shimmer */
.btn-add-cart {
  position: relative;
  overflow: hidden;
}
.btn-add-cart::after {
  content: '';
  position: absolute;
  top: 0; left: -100%;
  width: 60%; height: 100%;
  background: linear-gradient(120deg, transparent, rgba(255,255,255,0.4), transparent);
  animation: shimmer 2.5s infinite;
}
@keyframes shimmer {
  0%   { left: -100%; }
  100% { left: 200%; }
}

/* Call - gentle bounce */
.btn-call {
  animation: bounce-soft 2s infinite;
}
@keyframes bounce-soft {
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-3px); }
}

/* WhatsApp - green glow */
.btn-whatsapp {
  animation: pulse-green 2s infinite;
}
@keyframes pulse-green {
  0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7); }
  50%       { box-shadow: 0 0 0 10px rgba(34, 197, 94, 0); }
}

/* Messenger - blue glow */
.btn-messenger {
  animation: pulse-blue 2s infinite;
}
@keyframes pulse-blue {
  0%, 100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.7); }
  50%       { box-shadow: 0 0 0 10px rgba(37, 99, 235, 0); }
}

/* Hover: সব বাটন hover এ scale up */
.btn-buy-now:hover,
.btn-add-cart:hover,
.btn-call:hover,
.btn-whatsapp:hover,
.btn-messenger:hover {
  transform: scale(1.03);
  transition: transform 0.2s ease;
}

        /* Mobile: force 11px base font size for all product detail content */
        @media (max-width: 767px) {
          .pdp-mobile-root,
          .pdp-mobile-root * {
            font-size: 11px !important;
            line-height: 1.4 !important;
          }
          /* Product price: 16px */
          .pdp-mobile-root .mobile-price {
            font-size: 16px !important;
            line-height: 1.2 !important;
          }
          /* Keep icons from shrinking too much */
          .pdp-mobile-root svg {
            width: 11px !important;
            height: 11px !important;
          }
          /* Keep buttons legible but compact */
          .pdp-mobile-root button {
            min-height: 28px;
          }
        }
      `}</style>
    </>
  );
}

export default ProductDetails;
